import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import SearchIcon from '@mui/icons-material/Search';
import { useState } from "react";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import useDebounce from "../Utilities/Hooks/UseDebounce";
import InputAdornment from "@mui/material/InputAdornment";


interface IProps {
    files: FileNodeViewModel[];
    setSelectedFileContent: (file: string | null) => void;
}

export default function Search(props: IProps) {
    const { files, setSelectedFileContent } = props;
    const [searchResults, setSearchResults] = useState<FileNodeViewModel[]>([]);
    const [selectedValueInSearch, setSelectedValueInSearch] = useState<string | null>();

    const search = (fileList: FileNodeViewModel[], searchTerm: string): FileNodeViewModel[] => {
        const results: FileNodeViewModel[] = [];

        if (searchTerm.trim() === "") {
            return files;
        }

        for (const node of fileList) {
            if (isInSearch(node, searchTerm)) {
                results.push(highlightSearchTerm(node, searchTerm));
            }
            if (node.children) {
                const childResults = search(node.children, searchTerm);
                if (childResults.length > 0) {
                    childResults.forEach((child) => { results.push(child) });
                }
            }
        }
        return results;
    };

    const isInSearch = (node: FileNodeViewModel, searchTerm: string): boolean | undefined => {
        return (node.label.toLowerCase().includes(searchTerm.toLowerCase())
            || node.fileContent?.toLowerCase().includes(searchTerm.toLowerCase()))
            && node.fileContent !== null;
    }

    const highlightSearchTerm = (node: FileNodeViewModel, searchTerm: string): FileNodeViewModel => {
        const regex = new RegExp(`(?<!<[^>]*)(${searchTerm})(?![^<]*>)`, 'gi');
        const updatedNode = { ...node, fileContent: node.fileContent?.replace(regex, '<mark style="background-color: yellow; color: black;">$1</mark>') ?? node.fileContent };
        return updatedNode;
    };

    const { debouncedFunction: debouncedSearch } = useDebounce((e: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = e.target.value;
        setSelectedFileContent(null);

        if (searchTerm.trim() === "") {
            setSearchResults([]);
        } else {
            setSearchResults(search([...files], searchTerm));
        }
    }, 300);

    const findItemById = (fileList: FileNodeViewModel[], id: string | null): void => {
        if (id == null) {
            setSelectedFileContent(null);
            return;
        }
        for (const node of fileList) {
            if (node.id === id) {
                if (node.fileContent !== null) {
                    setSelectedFileContent(node.fileContent);
                    setSelectedValueInSearch(node.id)
                }
                return;
            }
            if (node.children) {
                findItemById(node.children, id);
            }
        }
    };

    return (
        <Grid>
            <TextField
                fullWidth
                color="primary"
                size="small"
                variant="outlined"
                placeholder="Search..."
                onChange={debouncedSearch}
                slotProps={{
                    input: {
                        endAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    },
                }}
            />
            <List dense>
                {searchResults.length > 0 && searchResults.map((node, index) => {
                    return <ListItemButton key={"searchButton" + node.id + " " + index} selected={node.id === selectedValueInSearch} onClick={() => findItemById(searchResults, node.id)}>
                        <ListItemText key={"searchText" + node.id + " " + index} primary={node.label} />
                    </ListItemButton>;
                })}
            </List>
        </Grid>
    );
}
